import { type ApiClientOptions } from '@/infra/api-client'
import {
  delay,
  exhaustMap,
  filter,
  map,
  merge,
  type Observable,
  of,
  retry,
  skip,
} from 'rxjs'
import {
  enableDevTools,
  fromStreamingCall,
  patchGrpcClient,
} from '@/utils/grpc-util'
import { JobServicePromiseClient } from '@taxfyle/api-internal/internal/job_grpc_web_pb'
import { RealtimeServicePromiseClient } from '@taxfyle/api-internal/internal/realtime_grpc_web_pb'
import { share, startWith, switchMap } from 'rxjs/operators'
import {
  RealtimeEvent,
  StreamEventOptions,
} from '@taxfyle/api-internal/internal/realtime_pb'
import { Empty } from 'google-protobuf/google/protobuf/empty_pb'
import { isOnline$ } from '@/utils/browser-util'
import { JobProgressionServicePromiseClient } from '@taxfyle/api-internal/internal/job_progression_grpc_web_pb'
import { ConsultationServicePromiseClient } from '@taxfyle/api-internal/internal/consultation_grpc_web_pb'
import { JobScopeChangeServicePromiseClient } from '@taxfyle/api-internal/internal/job_scope_change_grpc_web_pb'
import { BookkeepingServicePromiseClient } from '@taxfyle/api-internal/internal/bookkeeping_grpc_web_pb'

import { DraftServicePromiseClient } from '@taxfyle/api-internal/internal/draft_grpc_web_pb'
import { InfoGatheringServicePromiseClient } from '@taxfyle/api-internal/internal/info_gathering_grpc_web_pb'
import { JobRatingServicePromiseClient } from '@taxfyle/api-internal/internal/job_rating_grpc_web_pb'
import { TxfJobServicePromiseClient } from '@taxfyle/api-internal/internal/txf_grpc_web_pb'

export class JobsApi {
  readonly jobs
  readonly progressions
  readonly consultations
  readonly scopeChangeRequests
  readonly bookkeeping
  readonly draft
  readonly infoGathering
  readonly ratings
  readonly txf
  readonly realtime$

  constructor(options: ApiClientOptions) {
    const baseURL = options.baseURL ?? ''

    this.jobs = new JobServicePromiseClient(baseURL)
    patchGrpcClient(this.jobs, options.token$)

    this.progressions = new JobProgressionServicePromiseClient(baseURL)
    patchGrpcClient(this.progressions, options.token$)

    this.consultations = new ConsultationServicePromiseClient(baseURL)
    patchGrpcClient(this.consultations, options.token$)

    this.scopeChangeRequests = new JobScopeChangeServicePromiseClient(baseURL)
    patchGrpcClient(this.scopeChangeRequests, options.token$)

    this.bookkeeping = new BookkeepingServicePromiseClient(baseURL)
    patchGrpcClient(this.bookkeeping, options.token$)

    this.draft = new DraftServicePromiseClient(baseURL)
    patchGrpcClient(this.draft, options.token$)

    this.infoGathering = new InfoGatheringServicePromiseClient(baseURL)
    patchGrpcClient(this.infoGathering, options.token$)

    this.ratings = new JobRatingServicePromiseClient(baseURL)
    patchGrpcClient(this.ratings, options.token$)

    this.txf = new TxfJobServicePromiseClient(baseURL)
    patchGrpcClient(this.txf, options.token$)

    const realtime = new RealtimeServicePromiseClient(baseURL)
    enableDevTools(realtime)
    this.realtime$ = options.token$.pipe(
      // We don't need to reconnect when the token changes,
      // hence the use of `exhaustMap`. The retry policy at the end
      // will re-subscribe to the source and give us a fresh
      // token if triggered.
      exhaustMap((token) =>
        fromStreamingCall(
          () =>
            realtime.streamEvents(new StreamEventOptions(), {
              Authorization: `Bearer ${token}`,
            }),
          /* errorOnEnd:*/ true,
        ),
      ),
      // Reconnect after 40 seconds of no activity
      realtimeRetryPolicy(40_000),
      share(),
    )
  }
}

/**
 * Realtime stream policy which
 * 1) Reconnects after a short delay if there are any errors.
 * 2) Reconnects if no messages are received within a specific grace period (server
 * is expected to send a ping every so often).
 * @param pingGracePeriodMs Amount of time to wait for a ping before reconnecting
 */
function realtimeRetryPolicy<T>(pingGracePeriodMs: number) {
  return (source$: Observable<T>) =>
    source$.pipe(
      // Emit a ping right, away otherwise we don't start the
      // pipe which emits an error if we don't receive a message.
      startWith(new RealtimeEvent().setPing(new Empty())),
      switchMap((value) =>
        merge(
          of(value),
          // throw an error after timeout if nothing is received
          of(1).pipe(
            delay(pingGracePeriodMs),
            map(() => {
              throw new Error(`No new thing after ${pingGracePeriodMs}ms.`)
            }),
          ),
        ),
      ),
      // Skip the fake ping.
      skip(1),
      // log and retry after some delay
      retry({
        // Only retry while we're online.
        delay: () => isOnline$.pipe(filter(Boolean), delay(1_000)),
      }),
    )
}
